import { useEffect, useState, useCallback } from 'react'
import { parseEther } from 'viem'
import { useSendTransaction } from 'wagmi'
import { publicClientWeb3 } from '@/app/model'
import { UserStore } from '@/entities/user'
import { IUser } from '@/entities/user/model/types'
import { contractRead, DEFAULT_PREC, parseAmount } from '@/shared/lib'
import useNotifier, { createCalls } from '@/shared/lib/use-notifier'
import { LangStore } from '@/shared/model/store'
import { ContractsStore } from '@/shared/model/store/contracts-store'
import {
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Label
} from '@/shared/ui'
import { AmountBlock } from '@/shared/ui/amount-block/amount-block'
import AmountInput from '@/shared/ui/amount-input/ui/amount-input'
import SearchUserComponent from '@/features/search-user/ui'

type TransferModalProps = {
	isPls?: boolean
	isStake?: boolean
	getData: () => void
	npo?: `0x${string}` | null
}

const TransferModal = ({ getData, isPls, isStake, npo }: TransferModalProps) => {
	const { langPack } = LangStore()
	const { userData } = UserStore()
	const { contracts } = ContractsStore()

	// State management
	const [quantity, setQuantity] = useState<number>()
	const [open, setOpen] = useState(false)
	const [balance, setBalance] = useState<bigint>(0n)

	const address = npo || userData?.address || ('' as `0x${string}`)

	// Hooks
	const [recipient, setRecipient] = useState<IUser | null>(null)

	const { sendTransaction, isSuccess: isTransactionSuccess } = useSendTransaction()
	const {
		writeContract,
		writeContractMulticall,
		isLoading: isContractLoading
	} = useNotifier({
		onSuccess: () => {
			getData()
			setOpen(false)
		}
	})

	// Derived state
	const isLoading = isContractLoading
	const isValidTransfer = recipient?.address && quantity && quantity > 0

	// Fetch balance based on token type
	const fetchBalance = useCallback(async () => {
		if (!open) return

		try {
			if (isStake) {
				const stakingBalance = await contractRead('Staking', 'balanceOf', [address])
				setBalance(stakingBalance)
			} else if (isPls) {
				const client = publicClientWeb3()
				const balance = await client.getBalance({ address })
				setBalance(balance)
			} else {
				const tokenBalance = await contractRead('SavvaToken', 'balanceOf', [address])
				setBalance(tokenBalance)
			}
		} catch (error) {
			console.error('Failed to fetch balance:', error)
		}
	}, [open, isStake, isPls, address])

	// Handle transfer submission
	const handleTransfer = useCallback(() => {
		if (!recipient?.address || !quantity) return

		const amount = parseAmount(quantity)
		const rec = recipient.address as `0x${string}`

		// Handle PLS transfer
		if (isPls) {
			sendTransaction({
				to: rec,
				value: parseEther(String(quantity))
			})
			return
		}

		const contractConfig = {
			address: isStake ? contracts.Staking.address : contracts.SavvaToken.address,
			abi: isStake ? contracts.Staking.abi : contracts.SavvaToken.abi,
			functionName: 'transfer',
			args: [rec, amount]
		}

		// Handle NPO multicall
		if (npo) {
			writeContractMulticall(npo, createCalls([contractConfig]))
		} else {
			writeContract(contractConfig)
		}
	}, [recipient, quantity, isPls, isStake, npo, contracts, sendTransaction, writeContract, writeContractMulticall])

	// Effects
	useEffect(() => {
		if (isTransactionSuccess) {
			getData()
			setOpen(false)
		}
	}, [isTransactionSuccess, getData])

	useEffect(() => {
		fetchBalance()
	}, [fetchBalance])

	// Dialog handlers
	const handleDialogChange = (isOpen: boolean) => {
		if (!isOpen) {
			setQuantity(undefined)
			setRecipient(null)
		}
		setOpen(isOpen)
	}

	return (
		<Dialog open={open} onOpenChange={handleDialogChange}>
			<DialogTrigger asChild>
				<Button className='w-full text-white' variant='default'>
					{langPack('action.transfer')}
				</Button>
			</DialogTrigger>

			<DialogContent className='sm:max-w-[525px]'>
				<DialogHeader>
					<DialogTitle>{langPack('staking.pass_user')}</DialogTitle>
				</DialogHeader>

				<div className='space-y-4'>
					<div className='grid w-full grid-cols-4 items-center gap-4'>
						<Label className='text-right'>{langPack('default.user_name')}</Label>
						<div className='col-span-3'>
							<SearchUserComponent onSelect={setRecipient} selected={recipient} />
						</div>
					</div>

					<AmountInput
						value={quantity}
						onChange={setQuantity}
						label={langPack('fund.quantity')}
						token={isPls ? 'PLS' : 'SAVVA'}
						disabled={isLoading}
						maxValue={Number(balance)}
						setMax={() => setQuantity(Number(balance) / DEFAULT_PREC)}
					/>

					<div className='grid grid-cols-4 items-center gap-4'>
						<Label className='text-right'>{langPack('default.balance')}</Label>
						<div className='flex items-start justify-start'>
							<AmountBlock amount={balance} size='sm' token={isPls ? 'PLS' : 'SAVVA'} />
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button
						onClick={handleTransfer}
						variant='orange'
						className='text-white'
						disabled={isLoading || !isValidTransfer}
						loading={isLoading}
					>
						{langPack('action.transfer')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default TransferModal
